import { useEffect, useState, forwardRef, useCallback, useRef, useImperativeHandle, useMemo } from "react";
import { captionStyle, inputStyle } from "styles/global-style";

/**
 * Unified PillDropdown component
 * 
 * @param {Object} props
 * @param {Array<{value: string, id: any}>} props.list - List of items to display
 * @param {any} props.selectedId - Selected item ID (controlled)
 * @param {string} props.selectedValue - Selected item value (alternative to selectedId)
 * @param {string} props.selectedLabelOverride - When selectedId is set but not in list (e.g. deleted user), show this label
 * @param {Function} props.onSelect - Callback when item is selected: (item) => {}
 * @param {Function} props.onClear - Callback when selection is cleared
 * @param {string} props.placeholder - Placeholder text
 * @param {number} props.prefixNumber - Prefix number for estimate fields
 * @param {boolean} props.hasPrefixNumber - Explicitly enable prefixNumber mode (required when using prefixNumber)
 * @param {boolean} props.allowFiltering - Allow filtering by typing, default: true
 * @param {Object} props.pillStyle - Custom styles for the input/pill (for pill-style appearance)
 * @param {boolean} props.light - Light theme variant
 * @param {boolean} props.white - White theme variant
 * @param {string} props.caption - Caption text above input
 * @param {boolean} props.nocaption - Hide caption even if provided
 * @param {Object} props.customStyle - Additional custom styles (use for full-width input-style appearance)
 * @param {boolean} props.left - Left-align text
 * @param {Function} props.onChange - onChange callback for real-time typing
 * @param {Function} props.onChangeDelay - onChange with delay
 * @param {Function} props.onKeyDown - Custom keydown handler
 * @param {boolean} props.autoFocus - Auto focus on mount
 * @param {string} props.value - Controlled value for input
 */
export default forwardRef(function PillDropdown(props, ref) {
  const {
    list: initialList = [],
    selectedId,
    selectedValue,
    selectedLabelOverride,
    onSelect,
    onClear,
    placeholder = "",
    prefixNumber,
    hasPrefixNumber = false,
    allowFiltering = true,
    pillStyle = {},
    light = false,
    white = false,
    caption,
    nocaption = false,
    customStyle = {},
    left = false,
    onChange,
    onChangeDelay,
    onKeyDown: customOnKeyDown,
    autoFocus = false,
    value: controlledValue,
  } = props;

  // Find selected item
  const selectedItem = selectedId !== undefined
    ? initialList.find(item => item.id === selectedId)
    : selectedValue !== undefined
    ? initialList.find(item => item.value === selectedValue)
    : null;

  // State
  const [hasFocus, setHasFocus] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [dropdownWidth, setDropdownWidth] = useState(null);
  const [trackedInput, setTrackedInput] = useState(""); // Track input when userIcon is showing

  // Refs
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const submitTimerRef = useRef(null);
  const resetInputTimerRef = useRef(null);

  // Computed values
  // Determine if using input-style (full width) vs pill-style (auto width)
  // If customStyle has width: "100%" or customStyle is provided without pillStyle, treat as input-style
  // Otherwise, use pill-style (default)
  const isInputStyle = customStyle && Object.keys(customStyle).length > 0 && 
    (customStyle.width === "100%" || customStyle.width === "100% !important" || 
     (!pillStyle || Object.keys(customStyle).length > Object.keys(pillStyle).length));
  
  // Display value: prefer controlledValue if provided, otherwise show filterText when typing, or selectedItem, or selectedLabelOverride when selectedId is set but not in list
  const displayValue = controlledValue !== undefined 
    ? controlledValue 
    : (hasFocus && filterText
      ? filterText // When typing, show what user is typing
      : (selectedItem
        ? (prefixNumber !== undefined && prefixNumber !== null
          ? `${prefixNumber} ${selectedItem.value}`
          : selectedItem.value)
        : (selectedId != null && selectedLabelOverride
          ? selectedLabelOverride
          : "")));

  // Determine if userIcon is showing (selectedItem or selectedLabelOverride exists, not in input-style, and not in prefixNumber mode)
  // PrefixNumber mode uses the input field, so we shouldn't track input separately
  const isUserIconShowing = !isInputStyle && !hasPrefixNumber && (selectedItem !== null || (selectedId != null && selectedLabelOverride));

  // Filter items based on filterText or trackedInput
  const filteredItems = useMemo(() => {
    // For prefixNumber mode, always show all items (number is prefix, not filter)
    if (hasPrefixNumber) {
      return initialList;
    }
    
    // When userIcon is showing, use trackedInput instead of filterText
    const searchText = isUserIconShowing && hasFocus ? trackedInput : filterText;
    
    // If not focused, no filtering, or no filter text, show all items
    if (!hasFocus || !allowFiltering || !searchText.trim()) {
      return initialList;
    }
    // Otherwise, filter by the search text
    const searchLower = searchText.toLowerCase();
    return initialList.filter(item =>
      item.value.toLowerCase().includes(searchLower)
    );
  }, [initialList, filterText, trackedInput, hasFocus, allowFiltering, hasPrefixNumber, isUserIconShowing]);

  // Update highlighted index when filtered items change
  useEffect(() => {
    if (hasFocus && filteredItems.length > 0) {
      // When userIcon is showing and trackedInput changes, reset to first item
      if (isUserIconShowing && trackedInput) {
        setHighlightedIndex(0);
      } else if (highlightedIndex === -1) {
        setHighlightedIndex(0);
      }
    }
  }, [filteredItems, hasFocus, highlightedIndex, isUserIconShowing, trackedInput]);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current || !hasFocus) return;
    
    if (isInputStyle) {
      // For input-style, position dropdown below the input wrapper
      // Get the input wrapper div (parent of input)
      const inputWrapper = inputRef.current?.parentElement;
      if (inputWrapper) {
        const inputWrapperRect = inputWrapper.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        // Calculate position relative to container
        const top = inputWrapperRect.bottom - containerRect.top + 2;
        const left = inputWrapperRect.left - containerRect.left;
        const width = inputWrapperRect.width;
        setDropdownPosition({ top, left });
        setDropdownWidth(width);
      } else if (inputRef.current) {
        // Fallback: use input element directly
        const inputRect = inputRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        const top = inputRect.bottom - containerRect.top + 2;
        const left = inputRect.left - containerRect.left;
        const width = inputRect.width;
        setDropdownPosition({ top, left });
        setDropdownWidth(width);
      }
    } else {
      // For pill-style, use fixed offset
      const top = caption ? 30 : 20;
      setDropdownPosition({ top, left: 0 });
    }
  }, [hasFocus, isInputStyle, caption]);

  useEffect(() => {
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [updateDropdownPosition]);

  // Handle item selection
  const handleSelect = useCallback((item) => {
    // If onChange is provided, call it with the selected value
    if (onChange) {
      setFilterText(item.value);
      onChange({ target: { value: item.value } });
    }
    
    // For prefixNumber mode, pass the number value (from filterText or prefixNumber) as second param
    if (onSelect) {
      if (hasPrefixNumber) {
        // Use filterText if user just typed, otherwise use prefixNumber prop
        const numberValue = filterText && /^\d+$/.test(filterText.trim()) 
          ? parseInt(filterText.trim(), 10) 
          : (prefixNumber || 0);
        // Pass item and the number value for backward compatibility
        onSelect(item, numberValue);
      } else {
        onSelect(item);
      }
    }
    
    setHasFocus(false);
    setFilterText("");
    setTrackedInput("");
    setHighlightedIndex(-1);
    
    // Clear the reset timer
    if (resetInputTimerRef.current) {
      clearTimeout(resetInputTimerRef.current);
      resetInputTimerRef.current = null;
    }
    
    // Blur after a short delay to allow click events to complete
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 0);
  }, [onChange, onSelect, hasPrefixNumber, prefixNumber, filterText]);

  // Handle clear
  const handleClear = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setFilterText("");
    if (onChange) {
      onChange({ target: { value: "" } });
    }
    
    if (onClear) {
      onClear();
    }
    
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange, onClear]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    
    // Ensure focus is maintained when typing
    if (!hasFocus) {
      setHasFocus(true);
    }
    
    if (hasPrefixNumber) {
      // For prefix number mode, extract only the number
      const match = value.match(/\d{0,3}/);
      setFilterText(match ? match[0] : "");
    } else {
      setFilterText(value);
    }
    
    if (onChange) {
      onChange(e);
    } else if (onChangeDelay) {
      if (submitTimerRef.current) {
        clearTimeout(submitTimerRef.current);
      }
      submitTimerRef.current = setTimeout(() => {
        onChangeDelay(e);
      }, 3000);
    }
    
    // Auto-highlight first match
    if (value.trim() && filteredItems.length > 0) {
      setHighlightedIndex(0);
    }
    }, [hasPrefixNumber, onChange, onChangeDelay, filteredItems.length, hasFocus]);

  // Handle focus
  const handleFocus = useCallback(() => {
    setHasFocus(true);
    if (inputRef.current) {
      inputRef.current.placeholder = "";
    }
    
    // When userIcon is showing, reset tracked input and start timer
    if (isUserIconShowing) {
      setTrackedInput("");
      if (resetInputTimerRef.current) {
        clearTimeout(resetInputTimerRef.current);
      }
      resetInputTimerRef.current = setTimeout(() => {
        setTrackedInput("");
      }, 3000);
    }
    
    // For input-style, show all items when focused if no filter text
    if (isInputStyle && !filterText) {
      setHighlightedIndex(-1);
    } else if (filteredItems.length > 0) {
      setHighlightedIndex(0);
    }
  }, [isInputStyle, filterText, filteredItems.length, isUserIconShowing]);

  // Handle blur
  const handleBlur = useCallback((e) => {
    // Check if the new focus target is within our component
    const relatedTarget = e.relatedTarget;
    const isWithinComponent = containerRef.current?.contains(relatedTarget) ||
                              dropdownRef.current?.contains(relatedTarget);
    
    if (!isWithinComponent) {
      setHasFocus(false);
      setFilterText("");
      setTrackedInput("");
      setHighlightedIndex(-1);
      
      // Clear the reset timer
      if (resetInputTimerRef.current) {
        clearTimeout(resetInputTimerRef.current);
        resetInputTimerRef.current = null;
      }
      
      if (inputRef.current && !inputRef.current.value.trim()) {
        inputRef.current.placeholder = placeholder;
      }
      
      // Submit delayed change if needed
      if (onChangeDelay && submitTimerRef.current) {
        clearTimeout(submitTimerRef.current);
        onChangeDelay({ target: { value: filterText } });
      }
    }
  }, [placeholder, onChangeDelay, filterText]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    // When userIcon is showing, track input for filtering
    if (isUserIconShowing && hasFocus) {
      // Handle special keys
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === "Escape" || e.key === "Tab") {
        // These will be handled below
      } else if (e.key === "Backspace") {
        // Remove last character from tracked input
        e.preventDefault();
        setTrackedInput(prev => prev.slice(0, -1));
        // Reset the 3-second timer
        if (resetInputTimerRef.current) {
          clearTimeout(resetInputTimerRef.current);
        }
        resetInputTimerRef.current = setTimeout(() => {
          setTrackedInput("");
        }, 3000);
        return;
      } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Regular character input
        e.preventDefault();
        setTrackedInput(prev => prev + e.key);
        // Reset the 3-second timer
        if (resetInputTimerRef.current) {
          clearTimeout(resetInputTimerRef.current);
        }
        resetInputTimerRef.current = setTimeout(() => {
          setTrackedInput("");
        }, 3000);
        return;
      }
    }
    
    // Custom handler for Tab
    if (e.key === "Tab" && customOnKeyDown) {
      e.preventDefault();
      customOnKeyDown(e);
      return;
    }
    
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev < filteredItems.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : filteredItems.length - 1
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0 && filteredItems[highlightedIndex]) {
      e.preventDefault();
      e.stopPropagation();
      handleSelect(filteredItems[highlightedIndex]);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
    
    // Call custom handler for other keys
    if (customOnKeyDown && e.key !== "Tab") {
      customOnKeyDown(e);
    }
  }, [filteredItems, highlightedIndex, handleSelect, customOnKeyDown, isUserIconShowing, hasFocus]);

  // Calculate input width for pill-style (auto-width)
  const [inputWidth, setInputWidth] = useState(10);
  const hiddenMeasureRef = useRef(null);

  useEffect(() => {
    if (!isInputStyle && hiddenMeasureRef.current) {
      const width = hiddenMeasureRef.current.getBoundingClientRect().width;
      setInputWidth(Math.max(10, width));
    }
  }, [displayValue, placeholder, isInputStyle]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    blur: () => {
      inputRef.current?.blur();
    },
    getRef: () => inputRef.current,
    getSelectedId: () => selectedItem?.id,
  }), [selectedItem]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (submitTimerRef.current) {
        clearTimeout(submitTimerRef.current);
      }
      if (resetInputTimerRef.current) {
        clearTimeout(resetInputTimerRef.current);
      }
    };
  }, []);

  // Sync controlled value
  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== filterText) {
      setFilterText(controlledValue);
    }
  }, [controlledValue, filterText]);

  // Determine input styles - use customStyle if provided (input-style), otherwise use pillStyle (pill-style)
  const inputStyles = isInputStyle
    ? {
        ...inputStyle,
        fontWeight: "bold",
        textAlign: left ? "left" : "center",
        color: white ? "#fff" : "initial",
        border: white ? "1px solid #ffffff33" : "1px solid #bbb",
        ...customStyle,
      }
    : {
        fontSize: 11,
        fontWeight: "bold",
        padding: "2px 4px",
        textAlign: "center",
        border: light ? "1px solid #ffffff33" : "none",
        outline: "none",
        width: inputWidth,
        minWidth: 20,
        cursor: hasFocus ? "initial" : "pointer",
        paddingRight: hasFocus && selectedItem ? 10 : 4,
        filter: selectedItem ? "none" : "grayscale(100%)",
        opacity: selectedItem ? 1 : 0.7,
        ...pillStyle,
      };

  const showClearButton = hasFocus && ((isInputStyle && filterText) || (!isInputStyle && selectedItem));

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: isInputStyle ? "column" : "row",
        alignItems: isInputStyle ? "stretch" : "center",
        flexGrow: isInputStyle ? 1 : 0,
      }}
    >
      {caption && !nocaption && (
        <div style={{ ...captionStyle, color: light || white ? "#ffffff88" : "#999" }}>
          {caption}
        </div>
      )}
      
      <div style={{ position: "relative", flexGrow: isInputStyle ? 1 : 0 }}>
        <input
          ref={inputRef}
          className={light || white ? "input-white" : "input"}
          placeholder={placeholder}
          style={inputStyles}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          tabIndex={0}
        />
        
        {showClearButton && (
          <div
            style={{
              position: "absolute",
              height: 14,
              width: 14,
              right: isInputStyle ? 2 : -14,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onMouseDown={handleClear}
          >
            <svg width={isInputStyle ? 10 : 13} height={isInputStyle ? 10 : 13} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15M5 5L15 15" stroke="#dc3545" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown list */}
      {hasFocus && filteredItems.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            display: "flex",
            flexDirection: "column",
            minWidth: isInputStyle ? (dropdownWidth ? `${dropdownWidth}px` : "100%") : "auto",
            width: isInputStyle ? "max-content" : "auto",
            zIndex: 1000,
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            marginTop: "2px",
            borderRadius: 8,
            padding: isInputStyle ? "6px 7px" : "3px 4px",
            boxSizing: "border-box",
            maxHeight: isInputStyle ? "200px" : "none",
            overflowY: isInputStyle ? "auto" : "visible",
          }}
          onMouseDown={(e) => {
            // Prevent blur when clicking dropdown
            e.preventDefault();
          }}
        >
          {filteredItems.map((item, i) => (
            <ListItem
              key={item.id !== undefined ? item.id : `item-${i}`}
              value={item.value}
              isHighlighted={i === highlightedIndex}
              selectCb={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(item);
              }}
            />
          ))}
        </div>
      )}

      {/* Hidden element for measuring width (pill-style only) */}
      {!isInputStyle && (
        <span
          ref={hiddenMeasureRef}
          style={{
            display: "inline-block",
            position: "absolute",
            fontSize: 11,
            visibility: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {displayValue || placeholder}
        </span>
      )}
    </div>
  );
});

// ListItem component
const ListItem = ({ value, isHighlighted, selectCb }) => {
  const [hover, setHover] = useState(false);

  return (
    <div
      style={{
        padding: "4px 12px",
        background: isHighlighted || hover ? "#00000011" : "transparent",
        cursor: "pointer",
        color: "black",
        borderRadius: 4,
        whiteSpace: "nowrap",
        fontSize: 13,
      }}
      onMouseDown={selectCb}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {value}
    </div>
  );
};
